<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="subTitle" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <McButtonBack href="/master/" />
      </div>

      <McGraphqlEditableTable
        v-show="isTableDataAvailable"
        ref="table-ref"
        trackBy="ibeaconId"
        :fields="fields"
        :apiClient="query"
        :searchable="true"
        :canCreate="false"
        :canDelete="false"
        :withCard="true"
        :hideCheckbox="true"
        :hasViewManager="true"
        :withFlex="false"
        @changeData="handleChange"
        @handleEditCancel="handleCancel"
      >
        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <McFilterSelect
            :defaultColumnOrder="initialFields"
            :currentColumnOrder="dataFields"
            :loading="disabled"
            @setColumn="handleSetDataFields"
          />
        </template>
        <template #battery="{ item }">
          <div class="battery-level-container">
            <b-alert
              v-if="item.battery !== null"
              :variant="getBatteryVariant(item.battery)"
              show
              class="mb-0 py-1 px-2 d-inline-block"
            >
              {{ item.battery }}%
            </b-alert>
            <span v-else>-</span>
          </div>
        </template>
        <template #name="{ item }">
          {{ item?.name === '' ? '-' : item?.name ?? '-' }}
        </template>
        <template #licensePlate="{ item }">
          {{ item?.vehicle?.licensePlate ?? '-' }}
        </template>
        <template #description="{ item }">
          <div v-if="item?.description">
            <b-tooltip :target="`ibeacon-${item?.ibeaconId}`" placement="auto">
              {{ item?.description }}
            </b-tooltip>
            <span :id="`ibeacon-${item?.ibeaconId}`" class="mc-ellipsis">
              {{ item?.description }}
            </span>
          </div>
          <span v-else>-</span>
        </template>
      </McGraphqlEditableTable>
      <div v-show="!isTableDataAvailable" class="empty-table-container">
        <McEmptyState
          :title="$t('master.list.ibeacon.emptyState')"
          :desc="$t('master.list.ibeacon.emptyStateDesc')"
          :hasButton="false"
          class="pb-4"
        />
      </div>
    </div>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import { Helper } from '@/mixin/helper'
import McButtonBack from '@/components/McButtonBack'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import McEmptyState from '@/components/McEmptyState'
import McFilterSelect from '@/components/McFilterSelect'
import { GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import { GET_IBEACONS, UPDATE_IBEACON } from '@/services/api/master/ibeacon'

export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGraphqlEditableTable,
    McEmptyState,
    McFilterSelect,
  },
  mixins: [Helper],
  data() {
    return {
      isTableDataAvailable: true,
      disabled: false,
    }
  },
  computed: {
    title() {
      return `${this.$t('master.ibeacon.title')}`
    },
    subTitle() {
      return `${this.$t('master.ibeacon.list')}`
    },
    fields() {
      return [
        {
          key: 'ibeaconId',
          label: this.$t('master.list.ibeacon.ibeaconId'),
          placeholder: this.$t('master.list.ibeacon.ibeaconId'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
          isLocked: true,
        },
        {
          key: 'battery',
          label: this.$t('master.list.ibeacon.batteryLevel'),
          placeholder: this.$t('master.list.ibeacon.batteryLevel'),
          thClass: 'mc-table--90',
          isSortable: false,
          isEditable: false,
          customColumn: true,
          type: 'badge',
        },
        {
          key: 'name',
          label: this.$t('master.list.ibeacon.ibeaconName'),
          placeholder: this.$t('master.list.ibeacon.ibeaconName'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          customColumn: true,
        },
        {
          key: 'licensePlate',
          label: this.$t('master.list.ibeacon.vehicle'),
          thClass: 'mc-table--170',
          placeholder: this.$t('master.list.ibeacon.vehicle'),
          isSortable: true,
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'id',
          customColumn: true,
          optionsGetter: this.resourcefulQuery.vehicle,
          optionLabelFn: ({ item }) => item.licensePlate,
          getInitialObject: (item) => {
            return { id: item.vehicle?.id, licensePlate: item.vehicle?.licensePlate }
          },
          customValueFn: (item) => item.vehicle?.id ?? null,
        },
        {
          key: 'description',
          label: this.$t('master.list.ibeacon.description'),
          placeholder: this.$t('master.list.ibeacon.description'),
          thClass: 'mc-table--170',
          isSortable: false,
          isEditable: true,
          customColumn: true,
        },
      ]
    },
    query() {
      return {
        get: async (filter) => {
          if (filter.orderBy) {
            filter.orderBy = filter.orderBy.map((orderBy) => {
              const parsed = JSON.parse(orderBy)
              if (parsed.licensePlate) {
                return JSON.stringify({ 'vehicle.licensePlate': parsed.licensePlate })
              }
              return orderBy
            })
          }
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_IBEACONS,
            variables: {
              filter: filter,
            },
          })
          const result = res.data.getIbeacons.getIbeacons
          if (result.data.length <= 0) {
            this.isTableDataAvailable = false
          }
          this.disabled = false
          return result
        },
        update: async (id, oldData, newData) => {
          const input = {
            ibeaconId: id,
            oldData: {
              vehicleId: oldData.vehicle !== null ? Number(oldData.vehicle.id) : null,
              name: oldData.name,
              description: oldData.description,
            },
            newData: {
              vehicleId: newData?.licensePlate !== null ? Number(newData.licensePlate) : null,
              name: newData.name,
              description: newData.description,
            },
          }

          if (JSON.stringify(input.oldData) === JSON.stringify(input.newData)) {
            return oldData
          }

          const res = await this.$apollo.mutate({
            mutation: UPDATE_IBEACON,
            variables: {
              input: input,
            },
          })
          let updatedData = res.data.updateIbeacon.updateIbeacon
          if (!res.data.updateIbeacon.isSuccessful) {
            if (res.data.updateIbeacon.message == 'Vehicle already has another device') {
              this.$mcToast.error(this.$t('global.alert.text.hasAnotherDevice'))
            } else {
              this.$mcToast.error(this.$t('global.alert.text.failChangeData'))
            }
            return
          }
          this.disabled = false
          return updatedData
        },
      }
    },
    resourcefulQuery() {
      return {
        vehicle: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_VEHICLES_PAGINATED,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehiclesPaginated.vehiclesPaginated
          const result = {
            metadata: {
              total_page: Math.ceil(data.pagination.total / data.pagination.show),
              total_count: data.pagination.total,
              count: data.pagination.show,
              page: opts.page,
            },
            data: {
              paginated_result: data.data,
            },
          }
          return result
        },
      }
    },
  },
  methods: {
    async handleChange() {
      this.disabled = true
    },
    async handleCancel() {
      this.disabled = false
    },
  },
}
</script>
